import React, { useState } from "react";
import GenericFormDialog, { Field } from "./GenericFormDialog";
import ClientIntakeDialog from "./clientRecord/ClientIntakeDialog";
import { LABELS } from "../platform/config";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { createClient } from "../services/ClientsService";
import { getUserId } from "../services/authService";
import { toast } from "react-toastify";
import { countries } from "../assets/utils/countries";

interface AddClientDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (client: any) => void;
  branches?: any[];
  initialBranch?: string | null;
}

const AddClientDialog: React.FC<AddClientDialogProps> = ({
  open,
  onClose,
  onCreated,
  branches = [],
  initialBranch,
}) => {
  const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
  // The client just created: offers the optional medical info and consents.
  const [intakeClient, setIntakeClient] = useState<{ id: string; fullName: string } | null>(null);

  const formFields: Field[] = [
    { name: "fullName", label: "Full Name", required: true },
    { name: "email", label: "Email" },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
      required: true,
    },
    { name: "phoneNumber", label: "Phone Number", required: true },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Verified", value: "Verified" },
        { label: "Unverified", value: "Unverified" },
      ],
      required: true,
    },
    {
      name: "gender",
      label: "Gender",
      type: "dropdown",
      options: [
        { label: "Male", value: "MALE" },
        { label: "Female", value: "FEMALE" },
      ],
    },
    { name: "dob", label: "Date of Birth", type: "date" },
    { name: "banner", label: "Client Banner", type: "file", required: false },
    {
      name: "branch",
      label: "Branch/Business",
      type: "dropdown",
      options: branches.map((branch) => ({
        label: branch.name || "Unknown Branch",
        value: branch.business_id || branch.id || branch._id,
      })),
      hidden: true,
    },
    // The optional medical / health info and consent step after saving: off
    // until pressed, in every build.
    {
      name: "addRecord",
      label: LABELS.medicalInfo,
      colSpan: 2,
      customRender: (value, onChange) => (
        <Button
          variant={value ? "contained" : "outlined"}
          disableElevation
          startIcon={value ? <CheckIcon /> : <AddIcon />}
          onClick={() => onChange(!value)}
          sx={{ textTransform: "none", justifyContent: "flex-start" }}
          fullWidth
        >
          {value
            ? `${LABELS.medicalInfo} and consent forms open after saving`
            : `Add ${LABELS.medicalInfo.toLowerCase()} and consent forms`}
        </Button>
      ),
    },
  ];

  return (
    <>
    <GenericFormDialog
      open={open}
      onClose={onClose}
      title={`Add ${LABELS.client}`}
      fields={formFields}
      initialValues={{
        status: "Verified",
        countryCode: "+961",
        branch: initialBranch || (branches[0] && (branches[0].business_id || branches[0].id || branches[0]._id)),
        banner: undefined,
        addRecord: false,
      }}
      onSubmit={async (values) => {
        try {
          const userId = getUserId();
          const bannerFile = values.banner;
          if (bannerFile instanceof File && bannerFile.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error("Image must be 2MB or smaller.");
            return;
          }

          const formData = new FormData();
          formData.append("full_name", values.fullName || "");
          if (values.email && values.email.trim()) {
            formData.append("email", values.email.trim());
          }
          formData.append("phone", values.phoneNumber || "");
          formData.append("country", values.countryCode || "");
          formData.append("active", values.status === "Verified" ? "true" : "false");
          if (values.gender) formData.append("gender", values.gender);
          if (values.dob) formData.append("DOB", values.dob);
          if (bannerFile instanceof File) formData.append("banner", bannerFile);

          const response = await createClient(formData as any);

          toast.success(`${LABELS.client} created successfully!`, { position: "top-right" });
          onClose();
          const created = (response && (response.client || response.data || response)) || {};
          if (created.id && values.addRecord) {
            setIntakeClient({ id: String(created.id), fullName: values.fullName || "" });
          }
          if (onCreated) {
            const createdClient =
              (response && (response.client || response.data || response)) || {};
            onCreated({
              ...createdClient,
              full_name: values.fullName,
              email: values.email,
              phone: values.phoneNumber,
              country: values.countryCode,
              active: values.status === "Verified",
              gender: values.gender,
              DOB: values.dob,
              banner: bannerFile instanceof File ? URL.createObjectURL(bannerFile) : createdClient.banner,
            });
          }
        } catch (error: any) {
          console.error("Error creating client:", error);
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error_description ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to create client. Please try again.";
          toast.error(errorMessage, { position: "top-right" });
        }
      }}
    />
    <ClientIntakeDialog client={intakeClient} onClose={() => setIntakeClient(null)} />
    </>
  );
};

export default AddClientDialog;
